import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { EpisodeQualityCandidate } from "./datasetEpisode";
import type { MemberRef } from "./member";
import type { PageQuery, PageResp } from "./page";

/**
 * 데이터셋 학습 진단(diagnostics) 조회 경계다.
 *
 * backend `service/diagnostics/read/schema.py` 와 같은 타입이다. 채널별 밴드 밖 비율은 선언하지
 * 않는다. 화면이 그리는 그리드·밴드에서 직접 계산한다. 그래서 표시와 숫자가 달라지지 않는다.
 *
 * 계약 요점:
 * - 미산출은 404 가 아니라 **200 + `not_computed`** 다. 화면은 "아직 측정 안 함"과 "볼 권한
 *   없음"을 구분해 렌더한다. 404 는 부재/가시성(dataset.not_found 등)뿐이다.
 * - 시리즈 본문(위상 그리드 배열)은 응답에 없다. signed URL(`url`)로 GCS 에서 직접 로드한다.
 */

export type DiagnosticsStatus =
  | "not_computed"
  | "processing"
  | "ready"
  | "partial"
  | "stale"
  | "failed"
  // 컬렉션(밴드 집계) 전용 상태다. 업로드가 밴드 집계를 껐다(`band_diagnostics_enabled=false`).
  // `not_computed`(아직 실행 안 함)와도, `failed`(실행했지만 실패)와도 다르다. 기다릴 결과가
  // 없으므로 진행 문구를 표시하지 않는다. 이 상태는 영구가 아니다. 운영 CLI 로 다시 켤 수 있다.
  // 에피소드 진단은 그 플래그와 관계없이 항상 실행된다. 그래서 에피소드 상세에는 이 값이 오지 않는다.
  | "declined";

/** 에피소드 상세의 상태다. 컬렉션 전용 `declined` 를 뺀 어휘다(BE read 스키마와 같은 경계). */
export type EpisodeDiagnosticsStatus = Exclude<DiagnosticsStatus, "declined">;

/**
 * 위상 시리즈 포인터다. 본문은 signed URL 로만 가리킨다. 본문이 직접 선언하는
 * 메타(schema_version·resample_points·channels)는 파서(`diagnosticsSeries`)가 검증한다.
 */
export type DiagnosticsSeriesResp = {
  key: string;
  status: DiagnosticsStatus;
  url: string | null;
  failure: DiagnosticsFailureResp | null;
};

/**
 * 개별 산출물의 상태를 보존하고, ready 상태의 URL만 사용한다. 전체 partial 여부와 독립적이다.
 * 항목이 없으면 미산출로 추정하지 않는다. 소비처는 조회할 정보가 없는 것으로 표시한다.
 */
export function diagnosticSeriesOf(
  series: readonly DiagnosticsSeriesResp[],
  key: string,
): DiagnosticsSeriesResp | null {
  const item = series.find((entry) => entry.key === key);
  return item === undefined ? null : { ...item, url: item.status === "ready" ? item.url : null };
}

/** 실패 사유다. 워커 단계 코드(`<stage>_failed`)를 담는다. 서버는 마지막 성공 결과와 따로 보존한다. */
export type DiagnosticsFailureResp = {
  code: string;
};

// 컬렉션 실패 사유(failure)는 선언하지 않는다. BE 에 컬렉션 수준의 사유 소스가 없다.
// 컬렉션 실패는 실제로 "모든 에피소드 실패"를 뜻한다.
export type CollectionDiagnosticsResp = {
  status: DiagnosticsStatus;
  computed_at: string | null;
  series: DiagnosticsSeriesResp[];
};

export type EpisodeDiagnosticDetailResp = {
  episode_uuid: string;
  // 일부 산출물만 준비되면 partial이다. 각 series는 독립적인 준비 상태를 가진다.
  diagnostics_status: EpisodeDiagnosticsStatus;
  computed_at: string | null;
  series: DiagnosticsSeriesResp[];
  failure: DiagnosticsFailureResp | null;
  // 단건 응답에는 카메라·상태 채널까지 모두 들어 있다.
  channels: ChannelIndicatorResp[];
  // 범주형 채널(action_source)의 값 구간이다. 서버는 진단 상태로 거르지 않고 포함한다.
  // 빈 목록의 뜻은 `diagnosticSegments.ts` 가 채널 구조와 함께 판정한다. 구 BE 는 이 필드를
  // 보내지 않는다.
  // @compat ZERG-299 until=be:a25ad232
  segments?: ChannelSegmentsResp[];
  // video 진단의 품질 후보다. 워커가 에피소드 진단과 따로 산출한다. 그래서 `diagnostics_status`
  // 와 관계없이 담긴다. 구 BE 는 이 필드를 보내지 않는다(`qualityCandidatesOf`).
  // @compat ZERG-890 until=be:6eac17c2
  quality_candidates?: QualityCandidatesResp;
};

/**
 * 에피소드 하나의 품질 후보다. 진단이 계산한 결함 후보를 리뷰어에게 제안한다.
 *
 * 리뷰어가 후보를 보고 결함 태그를 직접 붙인다. 후보와 태그는 연결되지 않는다. 검토 완료가
 * 그때 있던 후보를 종류 단위로 "확인함" 으로 기록한다(`checks`). 확인은 결함 판정이 아니다.
 * `state` 는 목록 필터 `quality_candidate` 와 같은 판정이다.
 */
export type QualityCandidatesResp = {
  state: EpisodeQualityCandidate;
  // 후보를 계산한 분석 규칙의 버전이다. 분석 값이 없으면 null 이다. 판정에는 쓰지 않는다.
  analysis_version: string | null;
  // 시작 시각 순이다. 확인한 후보도 담는다. 그래서 `state` 가 `none` 이어도 항목이 있을 수 있다.
  items: QualityCandidateResp[];
  // 종류별 확인 기록이다. 종류 하나에 기록은 많아야 하나다. 다시 검토 완료하면 덮어쓴다.
  checks: QualityCandidateCheckResp[];
};

/** 후보 구간 하나다. 초는 영상 첫 프레임부터 잰다. 그래서 영상 시각으로 그대로 쓴다. */
export type QualityCandidateResp = {
  // 후보 종류다. 리뷰어가 붙이는 결함 값과 같은 이름이다. 예) `video_freeze`
  kind: string;
  start_seconds: number;
  end_seconds: number;
};

/** 후보 종류 하나의 확인 기록이다. 확인한 사람과 시각은 그 검토 완료의 것과 같다. */
export type QualityCandidateCheckResp = {
  // 확인한 후보 종류다. 예) `video_freeze`
  kind: string;
  checked_at: string;
  // 확인한 사람이다. 그 member 가 삭제되었으면 null 이다.
  checked_by_member: MemberRef | null;
};

/**
 * 값 구간 원소 하나다. hatchery-data `Segment` 에서 화면이 쓰는 필드만 담는다.
 *
 * - `run`: 같은 정수 값이 이어진 행들
 * - `gap`: 샘플이 빠진 자리(행 0개)
 * - `invalid`: NaN·비정수 행
 * 시각은 첫 행과 마지막 행의 timestamp 다(초). 에피소드 기준이라 음수일 수 있다.
 * gap 의 시각은 앞 행과 뒤 행의 timestamp 다.
 */
export type SegmentResp = {
  kind: "run" | "gap" | "invalid" | (string & {});
  // run 의 정수 값이다. 정의 밖 값은 source 가 null 이다. 그래서 같은 값끼리 이을지는 이 값으로만
  // 판단한다.
  code: number | null;
  // `ActionSource` 이름. 예) `INTERVENTION`
  source: string | null;
  t_start: number | null;
  t_end: number | null;
  row_start: number;
  n_rows: number;
};

/**
 * 한 범주형 채널의 값 구간이다. 원소는 행 순서다.
 *
 * **구간은 관측 span 이다. 상태가 유지된 시간이 아니다.** `t_end` 는 그 값의 마지막 관측이다.
 * 다음 원소의 `t_start` 는 새 값의 첫 관측이다. 그래서 전환마다 샘플 간격 하나가 어느 원소에도
 * 들어가지 않는다. `actionSourceSegments` 가 ZOH 로 구간을 띠로 이어 붙인다.
 */
export type ChannelSegmentsResp = {
  // `<topic>::<name>` 전체 이름. 예) `auxiliary_vector::action_source`
  channel: string;
  dt_median_seconds: number | null;
  // NaN·비정수 행 수 / 정수이지만 `ActionSource` 에 없는 값의 행 수다.
  invalid_rows: number;
  unknown_rows: number;
  segments: SegmentResp[];
};

/**
 * 스칼라 지표 하나다. 값이 없는 축은 항목이 응답에서 빠진다(`value: null` 이 아니다).
 * 소비부는 키가 있는지로 "측정할 수 없었다"를 판정한다.
 */
export type ScalarIndicatorResp = {
  key: string;
  value: number | null;
};

/**
 * 채널 하나의 실측값이다. `scalars` 와 함께 오지만 뜻이 다르다.
 *
 * `scalars` 는 컬렉션 안에서만 뜻이 있는 값이다(밴드 대비 이탈·큐레이션). 이 값은 에피소드
 * 자체의 기록 사실이다. 모든 채널의 샘플 수·시간 범위·간격·결측을 제공한다.
 */
export type ChannelIndicatorResp = {
  // `<topic>::<name>` 전체 이름. 예) `action::hand_left`
  channel: string;
  n_rows: number;
  dt_median_seconds: number | null;
  gap_fraction: number;
  duration_seconds: number;
  t_min: number | null;
  t_max: number | null;
  non_monotonic_fraction: number;
  duplicate_fraction: number;
  dt_jitter_mad_ratio: number | null;
  non_finite_fraction: number;
};

/**
 * 에피소드 진단 표의 한 행이다. 집계 전이면 `scalars` 가 비어 있고 식별자(uuid)만 있다.
 *
 * `channels` 는 기록 통계가 있는 모든 채널의 샘플 수·시각 범위·기록 간격을 담는다.
 */
export type EpisodeDiagnosticRowResp = {
  episode_uuid: string;
  scalars: ScalarIndicatorResp[];
  channels: ChannelIndicatorResp[];
};

/**
 * 데이터셋 에피소드의 진단 행 한 페이지다.
 *
 * 값들은 **컬렉션 안에서만 뜻이 있다**(backend `read.py::_deviation_scalar`). 밴드 밖 비율은
 * 이 데이터셋의 밴드가 기준이다. 큐레이션 두 축은 이 데이터셋 상태 공간의 이웃이 기준이다.
 * 에피소드 uuid 로 좁히는 파라미터가 없다. 그래서 단건만 조회할 수 없다. 전체를 가져온 뒤 찾는다.
 */
export async function listEpisodeDiagnostics(
  datasetUuid: string,
  query: PageQuery = {},
): Promise<PageResp<EpisodeDiagnosticRowResp>> {
  const response = await apiClient.get<PageResp<EpisodeDiagnosticRowResp>>(
    apiRoutes.dataset.diagnosticsEpisodes(datasetUuid),
    { params: { page: query.page, size: query.size } },
  );
  return response.data;
}

// 진단 화면의 지표 키다(backend `docs/specs/dataset-learning-diagnostics.md`).
export const EPISODE_PHASE_ENVELOPE_KEY = "episode_phase_envelope";
export const REFERENCE_PHASE_BAND_KEY = "reference_phase_band";
// 구간별 운동 요약이다. 위상 파일과 독립적인 준비 상태를 가진다.
export const EPISODE_TIMELINE_KEY = "episode_timeline";

/**
 * 데이터셋 진단 요약을 조회한다. 404(부재·가시성)는 `null` 을 반환한다.
 * 진단은 참고용 오버레이다. 그래서 소비부는 밴드를 숨기는 것으로 폴백한다. 그 외 오류는 전파한다.
 */
export async function getCollectionDiagnostics(
  datasetUuid: string,
): Promise<CollectionDiagnosticsResp | null> {
  try {
    const response = await apiClient.get<CollectionDiagnosticsResp>(
      apiRoutes.dataset.diagnostics(datasetUuid),
    );
    return response.data;
  } catch (error) {
    if (isHttpClientError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * 데이터셋에 속한 에피소드의 진단 상세를 조회한다. 404(부재·가시성)는 `null` 을 반환한다.
 * 그 외 오류는 전파한다. 미산출은 200 + `diagnostics_status=not_computed` 로 온다.
 *
 * 에피소드를 빠르게 넘기면 떠난 에피소드의 요청이 남는다. `signal` 로 요청을 취소해야
 * 재시도도 함께 멈춘다.
 */
export async function getEpisodeDiagnostics(
  datasetUuid: string,
  episodeUuid: string,
  options: { signal?: AbortSignal } = {},
): Promise<EpisodeDiagnosticDetailResp | null> {
  try {
    const response = await apiClient.get<EpisodeDiagnosticDetailResp>(
      apiRoutes.dataset.episodeDiagnostics(datasetUuid, episodeUuid),
      { signal: options.signal },
    );
    return response.data;
  } catch (error) {
    if (isHttpClientError(error) && error.status === 404) {
      return null;
    }
    throw error;
  }
}
